<template>
  <div>
    <div class="chart_holder" v-if="chartData && chartData.length">
      <plotly :data="chartData" :layout="chartLayout" :config="chartConfig"/>
    </div>
  </div>
</template>

<script>
import { Plotly } from 'vue-plotly'
import defaultLayout from '@/components/helpers/plotly_style'
import ReportDataHelper from '@/components/helpers/ReportDataHelper'
import moment from 'moment'

export default {
  name: 'OneDimensionalScatterChartPlotly',
  components: {
    Plotly
  },
  data() {
    return {
      // Populated automatically by the parent at runtime
      statsData: null,
      liveData: null,
      related_ids: null,
      reportParams: null,
      report: null
    }
  },
  props: {
    /*
      "stats" or "live"
      If "stats", we use statsData and aggregator.
      If "live", we read from liveData.
    */
    data_source: {
      type: String,
      default: 'stats'
    },
    /*
      The field(s) we want aggregated or shown from stats/live data.
      Usually a single string like "DEVICE_DAILY_TOTAL_KMS".
    */
    statistics: {
      type: [Array, String],
      default: () => []
    },
    /*
      The property used for labeling devices in the scatter (only relevant in data_source="live").
      Typically something like "device_name" or "device_imei".
    */
    label_field: {
      type: String,
      default: 'device_name'
    },
    /*
      If provided, can have +/- prefix to control ascending/descending sort
      of devices by their aggregated values.
    */
    order_field: {
      type: String,
      default: null
    },
    /*
      Used only in liveData mode to indicate which key in liveData to iterate (e.g. "devices").
    */
    data_key: {
      type: String,
      default: 'devices'
    },
    /*
      Fallback for missing or null data.
    */
    null_values: {
      default: 0
    },
    /*
      How we combine multiple date values in statsData (sum, max, min, count).
    */
    aggregration_mode: {
      type: String,
      default: 'sum'
    },
    /*
      Multiply final numeric values by this factor (e.g. convert meters → km).
    */
    value_multiple: {
      type: Number,
      default: 1
    },
    /*
      Only show text next to each point if number of points ≤ this threshold.
      Otherwise, only show on hover.
    */
    text_display_threshold: {
      type: Number,
      default: 20
    },
    /*
      Omit any point whose aggregator result is ~0.
    */
    hideZeroValues: {
      type: Boolean,
      default: false
    },
    /*
      If true, highlight whichever device has the largest x‐value in a special color.
    */
    highlightMax: {
      type: Boolean,
      default: false
    },
    /*
      If true, force the x‐axis to start at 0 rather than auto.
      Negative values get clipped off if present.
    */
    fixZeroPoint: {
      type: Boolean,
      default: false
    },
    /*
      If true, draw a dashed vertical line at the min x‐value.
    */
    show_min_line: {
      type: Boolean,
      default: false
    },
    /*
      If true, draw a dashed vertical line at the max x‐value.
    */
    show_max_line: {
      type: Boolean,
      default: false
    },
    /*
      If true, draw a dashed vertical line at the average (mean) x‐value.
    */
    show_mean_line: {
      type: Boolean,
      default: false
    },
    /*
      Size of the markers in the scatter.
    */
    marker_size: {
      type: Number,
      default: 8
    },
    /*
      If true, add a horizontal box plot for the same data,
      overlaid at a small vertical offset.
    */
    showBoxPlot: {
      type: Boolean,
      default: false
    },
    /*
      Plot layout overrides.
    */
    layout: {
      type: Object,
      default: () => ({})
    },
    /*
      Plot config overrides.
    */
    config: {
      type: Object,
      default: () => ({ responsive: true })
    }
  },
  methods: {
    async getRequiredStats() {
      if (this.data_source === 'stats') {
        let stats = []
        if (this.statistics) {
          let newStats
          if (this.statistics instanceof Promise) {
            newStats = await this.statistics
          } else {
            newStats = this.statistics
          }
          stats = stats.concat(newStats).flat()
        }
        return stats
      }
      return []
    },
    async getRequiredDeviceProps() {
      if (this.data_source === 'live') {
        let fields = []
        if (this.statistics) {
          let newStats
          if (this.statistics instanceof Promise) {
            newStats = await this.statistics
          } else {
            newStats = this.statistics
          }
          fields = fields.concat(newStats).flat()
        }
        if (this.label_field) {
          fields.push(this.label_field)
        }
        return fields
      }
      return []
    },
    /*
      Gather aggregator values per device from statsData.
    */
    _aggregateStatsForOneDimension(statKey) {
      if (!this.statsData || !this.statsData.stats || !this.statsData.stats[statKey]) {
        return {}
      }
      const deviceDict = this.statsData.stats[statKey]
      const allDevicesResult = {}

      Object.keys(deviceDict).forEach(deviceId => {
        let aggregatorVal = null
        const dateMap = deviceDict[deviceId]

        this.statsData.labels.forEach(lbl => {
          const rawVal = dateMap[lbl] == null ? this.null_values : dateMap[lbl]
          const val = rawVal * this.value_multiple
          if (aggregatorVal === null) {
            aggregatorVal = val
          } else {
            switch (this.aggregration_mode) {
              case 'sum':
                aggregatorVal += val
                break
              case 'max':
                aggregatorVal = Math.max(aggregatorVal, val)
                break
              case 'min':
                aggregatorVal = Math.min(aggregatorVal, val)
                break
              case 'count':
                aggregatorVal += (val !== this.null_values ? 1 : 0)
                break
              default:
                aggregatorVal += val
            }
          }
        })
        allDevicesResult[deviceId] = aggregatorVal || 0
      })
      return allDevicesResult
    },
    /*
      For live data, pick the first statistic in `statistics` and read it from each device object.
    */
    _getLiveValuesForOneDimension() {
      if (!this.liveData || !this.liveData[this.data_key]) {
        return {}
      }
      const devicesObject = this.liveData[this.data_key]
      const statsArr = Array.isArray(this.statistics) ? this.statistics : [this.statistics]
      const statKey = statsArr.length ? statsArr[0] : null

      let result = {}
      Object.entries(devicesObject).forEach(([deviceKey, deviceObj]) => {
        let val = statKey
          ? ReportDataHelper.getNestedField(deviceObj, statKey, this.null_values)
          : this.null_values
        val = val * this.value_multiple
        result[deviceKey] = val
      })
      return result
    }
  },
  computed: {
    chartData() {
      // 1) figure out aggregator values
      let deviceToValue = {}
      if (this.data_source === 'stats') {
        const statsArr = Array.isArray(this.statistics) ? this.statistics : [this.statistics]
        if (!statsArr.length) return []
        const primaryStat = statsArr[0]
        deviceToValue = this._aggregateStatsForOneDimension(primaryStat)
      } else {
        deviceToValue = this._getLiveValuesForOneDimension()
      }

      let sortedDeviceIds = Object.keys(deviceToValue)
      if (!sortedDeviceIds.length) return []

      // 2) optional sorting
      if (this.order_field) {
        let orderDir = 1
        let fieldName = this.order_field
        if (fieldName.startsWith('-')) {
          orderDir = -1
          fieldName = fieldName.slice(1)
        } else if (fieldName.startsWith('+')) {
          fieldName = fieldName.slice(1)
        }
        // In a 1D scatter, we assume ordering by aggregator
        sortedDeviceIds.sort((a, b) => {
          return orderDir * (deviceToValue[a] - deviceToValue[b])
        })
      }

      // 3) build x, y, text
      let xVals = []
      let yVals = []
      let textVals = []
      sortedDeviceIds.forEach(deviceId => {
        let v = deviceToValue[deviceId]
        if (this.hideZeroValues && Math.abs(v) < 1e-9) {
          return
        }
        xVals.push(v)
        // Everything is at y=0 so it's one-dimensional
        yVals.push(0)

        // Label
        let deviceLabel = deviceId
        if (this.data_source === 'stats' && this.related_ids && this.related_ids[deviceId]) {
          deviceLabel = this.related_ids[deviceId]
        } else if (
          this.data_source === 'live' &&
          this.liveData &&
          this.liveData[this.data_key][deviceId]
        ) {
          deviceLabel = ReportDataHelper.getNestedField(
            this.liveData[this.data_key][deviceId],
            this.label_field,
            deviceId
          )
        }
        textVals.push(deviceLabel)
      })

      // 4) handle highlightMax
      let markerColors = null
      if (this.highlightMax && xVals.length > 0) {
        const maxIndex = xVals.reduce((acc, val, i) => (val > xVals[acc] ? i : acc), 0)
        markerColors = xVals.map((_, i) => (i === maxIndex ? '#1f77b4' : '#b0b0b0'))
      }

      // 5) decide mode for scatter
      const showPointText = xVals.length <= this.text_display_threshold
      const traceMode = showPointText ? 'markers+text' : 'markers'

      // 6) create scatter trace
      let scatterTrace = {
        x: xVals,
        y: yVals,
        type: 'scatter',
        mode: traceMode,
        text: showPointText ? textVals : null,
        textposition: 'top center',
        hoverinfo: 'text+x',
        hovertext: textVals,
        marker: {
          size: this.marker_size
        }
      }
      // apply highlight colors if needed
      if (markerColors) {
        scatterTrace.marker.color = markerColors
      }

      // 7) optionally add a box plot trace
      // We place it at a small offset in y (e.g. 0.2) so it won’t overlap exactly.
      // Hide its own points so we don’t duplicate the scatter display.
      let finalTraces = [scatterTrace]
      if (this.showBoxPlot && xVals.length > 1) {
        // orientation:'h' => x is the numeric dimension, y is the "vertical" axis
        // We'll just put them all at ~0.2 so it’s clearly above the scatter.
        const boxTrace = {
          type: 'box',
          orientation: 'h',
          name: 'Box Plot',
          x: xVals,
          y: xVals.map(() => 0.2),
          fillcolor: 'rgba(0,0,0,0)',
          boxpoints: 'outliers', // shows outlier points
          hoverinfo: 'x',
          line: { width: 2, color: '#888' }
        }
        finalTraces.push(boxTrace)
      }

      return finalTraces
    },

    chartLayout() {
      let newLayout = {
        ...defaultLayout,
        ...this.layout
      }

      // Make the chart shorter unless user explicitly sets something
      if (!newLayout.height) {
        newLayout.height = 300
      }

      // Force x‐axis to start at 0 if fixZeroPoint
      // We'll find the max to set the upper range
      if (this.fixZeroPoint && this.chartData.length > 0) {
        const xVals = this.chartData[0].x || []
        if (xVals.length > 0) {
          const maxVal = Math.max(...xVals)
          if (!newLayout.xaxis) newLayout.xaxis = {}
          newLayout.xaxis.range = [0, maxVal * 1.1]
          newLayout.xaxis.autorange = false
        }
      } else {
        // Otherwise ensure x-axis is still visible with a line & grid
        if (!newLayout.xaxis) newLayout.xaxis = {}
        if (newLayout.xaxis.autorange == null) {
          newLayout.xaxis.autorange = true
        }
      }

      // Show x-axis line & grid for context
      newLayout.xaxis.showline = true
      newLayout.xaxis.linecolor = '#888'
      newLayout.xaxis.showgrid = true
      newLayout.xaxis.gridcolor = '#444'
      if (!newLayout.xaxis.title) {
        newLayout.xaxis.title = 'Value'
      }

      // Hide y-axis by default, since this is a one-dimensional plot
      if (!newLayout.yaxis) {
        newLayout.yaxis = {}
      }
      newLayout.yaxis.visible = false
      newLayout.yaxis.showline = false
      newLayout.yaxis.showticklabels = false
      newLayout.yaxis.zeroline = false

      // If we have an array of x-values, we can add lines for min/max/mean
      const dataTrace = this.chartData[0] || {}
      const xVals = dataTrace.x || []
      if (xVals.length > 1) {
        const minVal = Math.min(...xVals)
        const maxVal = Math.max(...xVals)
        const meanVal =
          xVals.reduce((a, b) => a + b, 0) / (xVals.length || 1)

        if (!newLayout.shapes) {
          newLayout.shapes = []
        }
        if (!newLayout.annotations) {
          newLayout.annotations = []
        }

        // Show min line
        if (this.show_min_line) {
          newLayout.shapes.push({
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: minVal,
            x1: minVal,
            y0: 0,
            y1: 1,
            line: {
              dash: 'dash',
              color: 'purple',
              width: 2
            }
          })
          newLayout.annotations.push({
            x: minVal,
            y: 1.04,
            xref: 'x',
            yref: 'paper',
            showarrow: false,
            text: `Min: ${minVal.toFixed(2)}`,
            font: { color: 'purple', size: 12 }
          })
        }

        // Show max line
        if (this.show_max_line) {
          newLayout.shapes.push({
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: maxVal,
            x1: maxVal,
            y0: 0,
            y1: 1,
            line: {
              dash: 'dash',
              color: 'orange',
              width: 2
            }
          })
          newLayout.annotations.push({
            x: maxVal,
            y: 1.04,
            xref: 'x',
            yref: 'paper',
            showarrow: false,
            text: `Max: ${maxVal.toFixed(2)}`,
            font: { color: 'orange', size: 12 }
          })
        }

        // Show mean line
        if (this.show_mean_line) {
          newLayout.shapes.push({
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: meanVal,
            x1: meanVal,
            y0: 0,
            y1: 1,
            line: {
              dash: 'dash',
              color: 'red',
              width: 2
            }
          })
          newLayout.annotations.push({
            x: meanVal,
            y: 1.04,
            xref: 'x',
            yref: 'paper',
            showarrow: false,
            text: `Mean: ${meanVal.toFixed(2)}`,
            font: { color: 'red', size: 12 }
          })
        }

        // Give a little extra top margin for those annotation texts
        if (!newLayout.margin) {
          newLayout.margin = {}
        }
        if (!newLayout.margin.t || newLayout.margin.t < 60) {
          newLayout.margin.t = 60
        }
      }

      return newLayout
    },

    chartConfig() {
      return this.config
    }
  }
}
</script>

<style scoped>
.chart_holder {
  width: 100%;
  height: 100%;
  min-height: 300px; /* Reduced default height */
}
</style>
